import { useEffect, useState } from 'react';
import { resourcesAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ExternalLink, LifeBuoy, Loader2 } from 'lucide-react';

const CATEGORIES = [
  { value: 'video_tutorial', label: 'Video Tutorial' },
  { value: 'user_manual', label: 'User Manual' },
  { value: 'installation_guide', label: 'Installation Guide' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'faq', label: 'FAQ' },
  { value: 'warranty_terms', label: 'Warranty Terms' },
  { value: 'safety_info', label: 'Safety Information' },
  { value: 'specifications', label: 'Specifications' },
  { value: 'other', label: 'Other' },
];

const emptyForm = { title: '', description: '', category: 'video_tutorial', content: '', file_url: '' };

export const ResourceManagementPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const res = await resourcesAPI.getAll({ is_active: true });
      setResources(res.data || []);
    } catch (e) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResources(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ title: r.title || '', description: r.description || '', category: r.category || 'other', content: r.content || '', file_url: r.file_url || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await resourcesAPI.update(editing.id, form);
        toast.success('Resource updated');
      } else {
        await resourcesAPI.create(form);
        toast.success('Resource added');
      }
      setDialogOpen(false);
      fetchResources();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save resource');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await resourcesAPI.remove(r.id);
      toast.success('Resource deleted');
      fetchResources();
    } catch (e) {
      toast.error('Failed to delete resource');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="resource-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight flex items-center gap-2">
            <LifeBuoy className="h-7 w-7 text-blue-600" /> Customer Resources
          </h1>
          <p className="text-muted-foreground mt-1">Manage videos, FAQs, guides and tools shown in the customer portal.</p>
        </div>
        <Button onClick={openNew} data-testid="add-resource-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Resource
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
          ) : resources.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No resources yet. Click &quot;Add Resource&quot; to create one.</div>
          ) : (
            <div className="divide-y">
              {resources.map(r => (
                <div key={r.id} className="flex items-center justify-between p-4" data-testid={`resource-item-${r.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{r.title}</span>
                      <Badge variant="outline" className="text-xs">{(CATEGORIES.find(c => c.value === r.category) || {}).label || r.category}</Badge>
                    </div>
                    {r.description && <p className="text-sm text-muted-foreground truncate">{r.description}</p>}
                    {r.file_url && (
                      <a href={r.file_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 inline-flex items-center gap-1 hover:underline">
                        {r.file_url} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-resource-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(r)} data-testid={`delete-resource-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Resource' : 'Add Resource'}</DialogTitle>
            <DialogDescription>This will appear in the customer portal Resources section.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} className="mt-1" data-testid="resource-title-input" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="mt-1" data-testid="resource-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1" data-testid="resource-description-input" />
            </div>
            <div>
              <Label>Link / Video URL</Label>
              <Input placeholder="https://youtube.com/... or a document link" value={form.file_url} onChange={(e) => setForm(p => ({ ...p, file_url: e.target.value }))} className="mt-1" data-testid="resource-url-input" />
            </div>
            <div>
              <Label>Notes / Content (optional)</Label>
              <Textarea rows={3} value={form.content} onChange={(e) => setForm(p => ({ ...p, content: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-resource-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save Changes' : 'Add Resource')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceManagementPage;
